import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import { CatSleeping } from '@/components/mascot'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { apiV2 } from '@/lib/api-v2-client'
import { ProOnlyPage } from '@/components/pro-only-page'
import { toast } from 'sonner'
import { listAllMonitors } from '@/lib/list-all-monitors'

export const Route = createFileRoute('/_authenticated/dashboard/maintenance')({
  component: () => (
    <ProOnlyPage
      title="Maintenance windows are on Pro"
      text="Plan downtime windows that pause alerts and show a banner on your status page. They need a paid plan."
    >
      <MaintenancePage />
    </ProOnlyPage>
  ),
})

function MaintenancePage() {
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)

  const { data: windowsResponse, isLoading } = useQuery({
    queryKey: ['maintenance-windows'],
    queryFn: () => apiV2.listMaintenanceWindows(),
  })

  const { data: monitorsResponse } = useQuery({
    queryKey: ['monitors', 'all'],
    queryFn: () => listAllMonitors(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiV2.deleteMaintenanceWindow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-windows'] })
      toast.success('Maintenance window deleted')
    },
    onError: () => toast.error('Failed to delete maintenance window'),
  })

  const completeMutation = useMutation({
    mutationFn: (id: string) => apiV2.updateMaintenanceWindow(id, { status: 'completed' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-windows'] })
      toast.success('Maintenance window completed')
    },
    onError: () => toast.error('Failed to complete maintenance window'),
  })

  const windows = windowsResponse?.data ?? []
  const monitors = monitorsResponse?.data ?? []

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold">Maintenance Windows</h1>
          <p className="text-muted-foreground">
            Schedule maintenance to suppress alerts during planned downtime
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>Schedule Maintenance</Button>
          </DialogTrigger>
          <DialogContent>
            <CreateMaintenanceForm
              monitors={monitors}
              onSuccess={() => {
                setCreateOpen(false)
                queryClient.invalidateQueries({ queryKey: ['maintenance-windows'] })
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Windows</CardTitle>
          <CardDescription>Active and scheduled maintenance windows</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : windows.length === 0 ? (
            <div className="py-8 text-center">
              <CatSleeping size={80} className="mx-auto mb-3" />
              <p className="text-muted-foreground">
                No maintenance windows scheduled
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Monitor</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead>End</TableHead>
                  <TableHead>Recurrence</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {windows.map((w) => (
                  <TableRow key={w.id}>
                    <TableCell className="font-medium">{w.title}</TableCell>
                    <TableCell>
                      {w.monitor_id
                        ? monitors.find((m) => m.id === w.monitor_id)?.name ?? 'Unknown'
                        : 'All monitors'}
                    </TableCell>
                    <TableCell>{formatDateTime(w.start_time)}</TableCell>
                    <TableCell>{formatDateTime(w.end_time)}</TableCell>
                    <TableCell>
                      {w.recurrence !== 'none' ? (
                        <Badge variant="outline" className="gap-1 capitalize">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3">
                            <path fillRule="evenodd" d="M12.75 2.5a.75.75 0 0 0-1.5 0v1.232a5 5 0 1 0 1.197 6.454.75.75 0 1 0-1.309-.726A3.5 3.5 0 1 1 10.065 5H8.75a.75.75 0 0 0 0 1.5h3a.75.75 0 0 0 .75-.75v-3z" clipRule="evenodd" />
                          </svg>
                          {w.recurrence}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">One-time</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={w.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        {w.status === 'active' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => completeMutation.mutate(w.id)}
                            disabled={completeMutation.isPending}
                          >
                            Complete
                          </Button>
                        )}
                        {w.status !== 'completed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteMutation.mutate(w.id)}
                            disabled={deleteMutation.isPending}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const variant = status === 'active' ? 'default' : status === 'scheduled' ? 'secondary' : 'outline'
  return <Badge variant={variant as 'default' | 'secondary' | 'outline'}>{status}</Badge>
}

function formatDateTime(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatNextOccurrence(startTime: string, recurrence: string): string {
  const start = new Date(startTime)
  if (isNaN(start.getTime())) return ''
  const next = new Date(start)
  switch (recurrence) {
    case 'daily':
      next.setDate(next.getDate() + 1)
      break
    case 'weekly':
      next.setDate(next.getDate() + 7)
      break
    case 'monthly':
      next.setMonth(next.getMonth() + 1)
      break
    default:
      return ''
  }
  return next.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function CreateMaintenanceForm({
  monitors,
  onSuccess,
}: {
  monitors: { id: string; name: string }[]
  onSuccess: () => void
}) {
  const [title, setTitle] = useState('')
  const [monitorId, setMonitorId] = useState('all')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [recurrence, setRecurrence] = useState('none')

  const createMutation = useMutation({
    mutationFn: () =>
      apiV2.createMaintenanceWindow({
        title,
        start_time: new Date(startTime).toISOString(),
        end_time: new Date(endTime).toISOString(),
        recurrence,
        ...(monitorId !== 'all' && { monitor_id: monitorId }),
      }),
    onSuccess: () => {
      toast.success('Maintenance window scheduled')
      onSuccess()
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to create maintenance window')
    },
  })

  return (
    <>
      <DialogHeader>
        <DialogTitle>Schedule Maintenance</DialogTitle>
        <DialogDescription>
          Alerts will be suppressed for affected monitors during this window
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label htmlFor="mw-title">Title</Label>
          <Input
            id="mw-title"
            placeholder="Server upgrade"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="mw-monitor">Affected Monitor</Label>
          <Select value={monitorId} onValueChange={setMonitorId}>
            <SelectTrigger id="mw-monitor">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All monitors</SelectItem>
              {monitors.map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="mw-start">Start Time</Label>
            <Input
              id="mw-start"
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mw-end">End Time</Label>
            <Input
              id="mw-end"
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="mw-recurrence">Recurrence</Label>
          <Select value={recurrence} onValueChange={setRecurrence}>
            <SelectTrigger id="mw-recurrence">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">One-time</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
          {recurrence !== 'none' && startTime && (
            <p className="text-xs text-muted-foreground">
              Next: {formatNextOccurrence(startTime, recurrence)}
            </p>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button
          onClick={() => createMutation.mutate()}
          disabled={!title || !startTime || !endTime || createMutation.isPending}
        >
          {createMutation.isPending ? 'Scheduling...' : 'Schedule'}
        </Button>
      </DialogFooter>
    </>
  )
}
